import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { Toaster } from "./components/ui/sonner";

// New auth & shell — eager (used on every public navigation)
import { AuthProvider } from "./contexts/AuthContext";
import { ModeProvider } from "./contexts/ModeContext";
import { RequireRole, RequireAnalystAccess } from "./components/auth/RouteGuards";
import PublicShell from "./components/PublicShell";
import { SubscriptionProvider } from "./contexts/SubscriptionContext";
import ConsentBanner from "./components/ConsentBanner";
import PageviewTracker from "./components/PageviewTracker";

// Public/standard-mode pages — eager (visitors hit these without auth)
import SignIn from "./pages/SignIn";
import SignUp from "./pages/SignUp";
import AuthCallback from "./pages/AuthCallback";
import StandardLanding from "./pages/StandardLanding";
import { PrivacyPolicy, TermsOfUse } from "./pages/Legal";

// Standard-mode interior pages — lazy (split off from initial bundle).
const HighlightReel = lazy(() => import("./pages/HighlightReel"));
const ComparativeMatrix = lazy(() => import("./pages/ComparativeMatrix"));
const FAQHub = lazy(() => import("./pages/FAQHub"));
const AIQuery = lazy(() => import("./pages/AIQuery"));
const PostcodeChecker = lazy(() => import("./pages/PostcodeChecker"));
const Compare = lazy(() => import("./pages/Compare"));
const Reports = lazy(() => import("./pages/Reports"));
const LAIdentity = lazy(() => import("./pages/LAIdentity"));
const DsiLeaderboard = lazy(() => import("./pages/DsiLeaderboard"));
const BlogIndex = lazy(() => import("./pages/BlogIndex"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Profile = lazy(() => import("./pages/Profile"));
const Pricing = lazy(() => import("./pages/Pricing"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const PaymentCancel = lazy(() => import("./pages/PaymentCancel"));
const WhyUs = lazy(() => import("./pages/WhyUs"));

// Admin / Analyst Mode — lazy (chart, map, cytoscape, pdf libs are heavy).
const AdminOrders = lazy(() => import("./pages/AdminOrders"));
const AdminSubscriptions = lazy(() => import("./pages/AdminSubscriptions"));
const Layout = lazy(() => import("./components/Layout"));
const GraphView = lazy(() => import("./pages/GraphView"));
const GraphMapSync = lazy(() => import("./pages/GraphMapSync"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const DataManagement = lazy(() => import("./pages/DataManagement"));
const QueryInterface = lazy(() => import("./pages/QueryInterface"));
const ReportGenerator = lazy(() => import("./pages/ReportGenerator"));
const MapView = lazy(() => import("./pages/MapView"));
const AdminPortal = lazy(() => import("./pages/AdminPortal"));
const BlogAdmin = lazy(() => import("./pages/BlogAdmin"));

// Any authenticated user (admin · analyst · standard_user) may enter the
// Analyst shell — only the consumer-grade views (Map · Sync · Dashboard)
// are exposed by default. Admin-only tools (Graph / Data / Query / Reports /
// Blog) keep their per-route `ADMIN_ONLY` guard further down.
const ANALYST_ROLES = ["analyst", "admin", "standard_user"];
const ADMIN_ONLY = ["admin"];

/**
 * AnimatedPage — wraps each route with a soft Framer Motion page transition.
 */
function AnimatedPage({ children }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

/** Suspense fallback while a lazy chunk loads. */
function RouteFallback() {
  return (
    <div className="min-h-[50vh] flex items-center justify-center" data-testid="route-fallback">
      <Loader2 className="h-6 w-6 animate-spin text-[#5C5C59]" />
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <AnimatePresence mode="wait" initial={false}>
      <PageviewTracker />
      <Suspense fallback={<RouteFallback />}>
        <Routes location={location} key={location.pathname}>
          {/* Public / Standard mode shell */}
          <Route path="/" element={<PublicShell />}>
            <Route index element={<AnimatedPage><StandardLanding /></AnimatedPage>} />
            <Route path="ai" element={<AnimatedPage><AIQuery /></AnimatedPage>} />
            <Route path="postcode" element={<AnimatedPage><PostcodeChecker /></AnimatedPage>} />
            <Route path="compare" element={<AnimatedPage><Compare /></AnimatedPage>} />
            <Route path="highlights" element={<AnimatedPage><HighlightReel /></AnimatedPage>} />
            <Route path="reports" element={<AnimatedPage><Reports /></AnimatedPage>} />
            <Route path="blog" element={<AnimatedPage><BlogIndex /></AnimatedPage>} />
            <Route path="blog/:slug" element={<AnimatedPage><BlogPost /></AnimatedPage>} />
            <Route path="faq" element={<AnimatedPage><FAQHub /></AnimatedPage>} />
            <Route path="why-us" element={<AnimatedPage><ComparativeMatrix /></AnimatedPage>} />
            <Route path="why-us-pilot" element={<AnimatedPage><WhyUs /></AnimatedPage>} />
            <Route path="la/:slug" element={<AnimatedPage><LAIdentity /></AnimatedPage>} />
            <Route path="dsi" element={<AnimatedPage><DsiLeaderboard /></AnimatedPage>} />
            <Route path="privacy" element={<AnimatedPage><PrivacyPolicy /></AnimatedPage>} />
            <Route path="terms" element={<AnimatedPage><TermsOfUse /></AnimatedPage>} />
            <Route path="pricing" element={<AnimatedPage><Pricing /></AnimatedPage>} />
            <Route path="payments/success" element={<AnimatedPage><PaymentSuccess /></AnimatedPage>} />
            <Route path="payments/cancel" element={<AnimatedPage><PaymentCancel /></AnimatedPage>} />
            <Route path="profile" element={<AnimatedPage><Profile /></AnimatedPage>} />
          </Route>

          {/* Auth pages — split layout, no shell */}
          <Route path="/signin" element={<SignIn />} />
          <Route path="/signup" element={<SignUp />} />

          {/* Analyst Mode — gated by Premium/Enterprise subscription OR admin/analyst role */}
          <Route
            path="/analyst"
            element={
              <RequireAnalystAccess>
                <div className="App dark">
                  <Layout />
                </div>
              </RequireAnalystAccess>
            }
          >
            <Route index element={<Navigate to="/analyst/dashboard" replace />} />
            {/* Analyst-accessible workspace pages */}
            <Route path="map" element={<MapView />} />
            <Route path="sync" element={<GraphMapSync />} />
            <Route path="dashboard" element={<Dashboard />} />
            {/* Admin-only — moved into the Admin scope but kept under /analyst/* paths */}
            <Route
              path="graph"
              element={<RequireRole roles={ADMIN_ONLY}><GraphView /></RequireRole>}
            />
            <Route
              path="data"
              element={<RequireRole roles={ADMIN_ONLY}><DataManagement /></RequireRole>}
            />
            <Route
              path="query"
              element={<RequireRole roles={ADMIN_ONLY}><QueryInterface /></RequireRole>}
            />
            <Route
              path="reports"
              element={<RequireRole roles={ADMIN_ONLY}><ReportGenerator /></RequireRole>}
            />
            <Route
              path="blog"
              element={<RequireRole roles={ADMIN_ONLY}><BlogAdmin /></RequireRole>}
            />
          </Route>

          {/* Admin portal — unified-auth gated (no more separate /admin/login) */}
          <Route path="/admin/login" element={<Navigate to="/signin" replace state={{ from: { pathname: "/admin" } }} />} />
          <Route
            path="/admin/orders"
            element={<RequireRole roles={ADMIN_ONLY}><AdminOrders /></RequireRole>}
          />
          <Route
            path="/admin/subscriptions"
            element={<RequireRole roles={ADMIN_ONLY}><AdminSubscriptions /></RequireRole>}
          />
          <Route
            path="/admin/*"
            element={
              <RequireRole roles={ADMIN_ONLY}>
                <AdminPortal />
              </RequireRole>
            }
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </AnimatePresence>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SubscriptionProvider>
          <ModeProvider>
            <AppRouter />
          </ModeProvider>
        </SubscriptionProvider>
      </AuthProvider>
      <Toaster position="top-right" />
      <ConsentBanner />
    </BrowserRouter>
  );
}
